import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Copy, Trash2 } from "lucide-react";
import useDebounce from "../../../hooks/useDebounce";
import {
  AVAILABILITY_WINDOWS,
  CHECK_IN_TIME_OPTIONS,
  ROOM_AMOUNT_OPTIONS,
  SAUDI_COUNTRY,
  catalogLabel,
  resizeTypeArray,
} from "./constants";
import ChipGroup from "./ChipGroup";
import { lookupAccountByMobile, searchCities } from "./apiHelpers";
import { getListingIssues, parseLatLng } from "./validation";

const fieldClass =
  "w-full rounded-md border border-gray-400 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-darkBg2 dark:text-[#c7d8eb]";
const labelClass = "mb-1 block text-sm font-medium";
const sectionClass =
  "rounded-md border border-gray-200 p-4 dark:border-gray-700";

const Field = ({ label, hint, hintColor, children, className = "" }) => (
  <div className={className}>
    <label className={labelClass}>{label}</label>
    {children}
    {hint ? (
      <p
        className="mt-1 text-xs"
        style={{ color: hintColor || undefined }}
      >
        <span className={!hintColor ? "text-gray-500 dark:text-gray-400" : ""}>
          {hint}
        </span>
      </p>
    ) : null}
  </div>
);

const YesNo = ({ value, onChange }) => (
  <div className="flex gap-2">
    {[
      { label: "Yes", on: true },
      { label: "No", on: false },
    ].map((opt) => (
      <button
        key={opt.label}
        type="button"
        onClick={() => onChange(opt.on)}
        className={`min-w-[72px] rounded-md border px-3 py-2 text-sm ${
          value === opt.on
            ? "border-secondaryColor bg-secondaryColor text-white"
            : "border-gray-400 dark:border-gray-600"
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const ListingCard = ({
  listing,
  index,
  catalogs,
  vatPercent,
  canRemove,
  onChange,
  onDuplicate,
  onRemove,
}) => {
  const [cityQuery, setCityQuery] = useState(listing.cityEn || "");
  const [cityOptions, setCityOptions] = useState([]);
  const [cityOpen, setCityOpen] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const cityRef = useRef(null);
  const debouncedCity = useDebounce(cityQuery, 300);
  const debouncedMobile = useDebounce(listing.mobileNumber, 450);

  const issues = getListingIssues(listing);
  const ready = issues.length === 0;
  const coords = parseLatLng(listing.latlng);

  useEffect(() => {
    const onDoc = (e) => {
      if (cityRef.current && !cityRef.current.contains(e.target)) {
        setCityOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    let alive = true;
    setCityLoading(true);
    searchCities(debouncedCity)
      .then((cities) => {
        if (alive) setCityOptions(cities);
      })
      .catch(() => {
        if (alive) setCityOptions([]);
      })
      .finally(() => {
        if (alive) setCityLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [debouncedCity]);

  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        const result = await lookupAccountByMobile(debouncedMobile);
        if (!alive) return;
        if (result.status === "found") {
          onChange({
            accountOk: true,
            accountHint: result.message,
            userId: result.userId,
          });
        } else if (result.status === "multiple") {
          onChange({
            accountOk: false,
            accountHint: result.message,
          });
        } else if (result.status === "none") {
          onChange({
            accountOk: false,
            accountHint: result.message,
            userId: "",
          });
        } else {
          // short / cleared mobile — reset account + user id
          onChange({
            accountOk: false,
            accountHint: result.message || "",
            userId: "",
          });
        }
      } catch {
        if (alive) {
          onChange({
            accountOk: false,
            accountHint: "Could not look up this number",
          });
        }
      }
    };
    run();
    return () => {
      alive = false;
    };
    // Only re-run when the debounced mobile value changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedMobile]);

  const set = (patch) => onChange(patch);
  const setField = (key) => (e) => set({ [key]: e.target.value });

  const bedsTotal = Math.min(
    30,
    Math.max(0, Number.parseInt(listing.bedsTotal, 10) || 0),
  );
  const bathsTotal = Math.min(
    30,
    Math.max(0, Number.parseInt(listing.bathroomsTotal, 10) || 0),
  );

  const meta =
    [
      catalogLabel(
        (catalogs.propertyTypes || []).find((p) => p.id === listing.propertyType),
      ),
      listing.cityEn,
      listing.basePrice ? `${listing.basePrice} SAR` : "",
    ]
      .filter(Boolean)
      .join(" · ") || "Not filled yet";

  const toggleChip = (key, id) => {
    const current = listing[key] || [];
    set({
      [key]: current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id],
    });
  };

  return (
    <article className="overflow-hidden rounded-md border border-gray-400 dark:border-white">
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-300 px-4 py-3 dark:border-gray-600">
        <button
          type="button"
          onClick={() => set({ open: !listing.open })}
          className="flex flex-1 items-center gap-3 text-left"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondaryColor/15 text-sm font-bold text-secondaryColor">
            {index + 1}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold">
              {listing.title || "Untitled listing"}
            </div>
            <div className="truncate text-sm text-gray-500 dark:text-gray-400">
              {meta}
            </div>
          </div>
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-medium ${
              ready
                ? "bg-green-100 text-green-800"
                : "bg-amber-100 text-amber-900"
            }`}
          >
            {ready ? "Ready" : "Needs attention"}
          </span>
          <ChevronDown
            size={18}
            className={`transition-transform ${listing.open ? "rotate-180" : ""}`}
          />
        </button>
        <button
          type="button"
          onClick={onDuplicate}
          className="flex items-center gap-1 rounded-md border border-secondaryColor px-3 py-1.5 text-sm text-secondaryColor"
        >
          <Copy size={14} />
          Duplicate
        </button>
        <button
          type="button"
          disabled={!canRemove}
          onClick={onRemove}
          className="flex items-center gap-1 rounded-md border border-red-400 px-3 py-1.5 text-sm text-red-600 disabled:opacity-40"
        >
          <Trash2 size={14} />
          Remove
        </button>
      </div>

      {listing.open && (
        <div className="flex flex-col gap-4 p-4">
          {!ready && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              To fix: {issues.join(", ")}
            </div>
          )}
          {(listing.serverErrors || []).length > 0 && (
            <div
              role="alert"
              className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800"
            >
              <ul className="list-inside list-disc">
                {listing.serverErrors.map((err, i) => (
                  <li key={`${err.field}-${i}`}>
                    {err.field}: {err.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <section className={sectionClass}>
            <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-secondaryColor">
              Client account
            </h3>
            <p className="mb-3 text-sm text-gray-500">Who owns these listings.</p>
            <div className="grid gap-3 md:grid-cols-2">
              <Field
                label="Client mobile number"
                hint={listing.accountHint || "Type the client mobile and the account is found for you"}
                hintColor={
                  listing.accountOk
                    ? "#067647"
                    : listing.accountHint?.startsWith("No ")
                      ? "#B42318"
                      : undefined
                }
              >
                <input
                  className={fieldClass}
                  value={listing.mobileNumber}
                  onChange={(e) => {
                    const mobileNumber = e.target.value;
                    if (!String(mobileNumber).trim()) {
                      set({
                        mobileNumber: "",
                        userId: "",
                        accountOk: false,
                        accountHint: "",
                      });
                      return;
                    }
                    set({ mobileNumber });
                  }}
                  placeholder="5xxxxxxxx"
                />
              </Field>
              <Field
                label="Account ID (optional)"
                hint="Required only when multiple accounts share this number"
              >
                <input
                  className={fieldClass}
                  value={listing.userId}
                  onChange={(e) =>
                    set({
                      userId: e.target.value,
                      accountOk: Boolean(e.target.value.trim()) || listing.accountOk,
                    })
                  }
                  placeholder="MongoDB user id"
                />
              </Field>
            </div>
          </section>

          <section className={sectionClass}>
            <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-secondaryColor">
              Property
            </h3>
            <p className="mb-3 text-sm text-gray-500">What kind of place it is.</p>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Property type">
                <select
                  className={fieldClass}
                  value={listing.propertyType}
                  onChange={setField("propertyType")}
                >
                  <option value="">Choose…</option>
                  {(catalogs.propertyTypes || []).map((item) => (
                    <option key={item.id} value={item.id}>
                      {catalogLabel(item)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Room type">
                <select
                  className={fieldClass}
                  value={listing.roomType}
                  onChange={setField("roomType")}
                >
                  <option value="">Choose…</option>
                  {(catalogs.roomTypes || []).map((item) => (
                    <option key={item.id} value={item.id}>
                      {catalogLabel(item)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Total units">
                <input
                  type="number"
                  min={1}
                  max={100}
                  className={fieldClass}
                  value={listing.totalUnits}
                  onChange={setField("totalUnits")}
                />
              </Field>
              <Field label="Room amount">
                <select
                  className={fieldClass}
                  value={listing.roomAmount}
                  onChange={setField("roomAmount")}
                >
                  {ROOM_AMOUNT_OPTIONS.map((n) => (
                    <option key={n} value={String(n)}>
                      {n}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Personal home">
                <YesNo
                  value={listing.personalHome}
                  onChange={(personalHome) => set({ personalHome })}
                />
              </Field>
            </div>
          </section>

          <section className={sectionClass}>
            <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-secondaryColor">
              Capacity and beds
            </h3>
            <p className="mb-3 text-sm text-gray-500">
              Guests, beds and bathrooms.
            </p>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Guests">
                <input
                  type="number"
                  min={1}
                  className={fieldClass}
                  value={listing.guests}
                  onChange={setField("guests")}
                />
              </Field>
              <Field label="Bedrooms">
                <input
                  type="number"
                  min={0}
                  className={fieldClass}
                  value={listing.bedrooms}
                  onChange={setField("bedrooms")}
                />
              </Field>
              <Field label="Total beds" hint="One dropdown appears per bed">
                <input
                  type="number"
                  min={1}
                  max={30}
                  className={fieldClass}
                  value={listing.bedsTotal}
                  onChange={(e) => {
                    const v = e.target.value;
                    const n = Math.min(
                      30,
                      Math.max(0, Number.parseInt(v, 10) || 0),
                    );
                    set({
                      bedsTotal: v,
                      bedTypes: resizeTypeArray(
                        listing.bedTypes,
                        n,
                        catalogs.bedTypes?.[0]?.id || "",
                      ),
                    });
                  }}
                />
              </Field>
              <Field
                label="Total bathrooms"
                hint="One dropdown appears per bathroom"
              >
                <input
                  type="number"
                  min={1}
                  max={30}
                  className={fieldClass}
                  value={listing.bathroomsTotal}
                  onChange={(e) => {
                    const v = e.target.value;
                    const n = Math.min(
                      30,
                      Math.max(0, Number.parseInt(v, 10) || 0),
                    );
                    set({
                      bathroomsTotal: v,
                      bathroomTypes: resizeTypeArray(
                        listing.bathroomTypes,
                        n,
                        catalogs.bathroomTypes?.[0]?.id || "",
                      ),
                    });
                  }}
                />
              </Field>
            </div>
            {bedsTotal > 0 && (
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {Array.from({ length: bedsTotal }).map((_, i) => (
                  <Field key={`bed-${i}`} label={`Bed ${i + 1} type`}>
                    <select
                      className={fieldClass}
                      value={listing.bedTypes[i] || ""}
                      onChange={(e) => {
                        const bedTypes = [...listing.bedTypes];
                        bedTypes[i] = e.target.value;
                        set({ bedTypes });
                      }}
                    >
                      <option value="">Choose…</option>
                      {(catalogs.bedTypes || []).map((item) => (
                        <option key={item.id} value={item.id}>
                          {catalogLabel(item)}
                        </option>
                      ))}
                    </select>
                  </Field>
                ))}
              </div>
            )}
            {bathsTotal > 0 && (
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {Array.from({ length: bathsTotal }).map((_, i) => (
                  <Field key={`bath-${i}`} label={`Bathroom ${i + 1} type`}>
                    <select
                      className={fieldClass}
                      value={listing.bathroomTypes[i] || ""}
                      onChange={(e) => {
                        const bathroomTypes = [...listing.bathroomTypes];
                        bathroomTypes[i] = e.target.value;
                        set({ bathroomTypes });
                      }}
                    >
                      <option value="">Choose…</option>
                      {(catalogs.bathroomTypes || []).map((item) => (
                        <option key={item.id} value={item.id}>
                          {catalogLabel(item)}
                        </option>
                      ))}
                    </select>
                  </Field>
                ))}
              </div>
            )}
          </section>

          <section className={sectionClass}>
            <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-secondaryColor">
              Location
            </h3>
            <p className="mb-3 text-sm text-gray-500">
              Country and city fill the Arabic names for you.
            </p>
            <div className="grid gap-3 md:grid-cols-2">
              <Field
                label="Country"
                hint={`Arabic name: ${SAUDI_COUNTRY.ar}`}
              >
                <input
                  className={fieldClass}
                  value={SAUDI_COUNTRY.en}
                  disabled
                />
              </Field>
              <div ref={cityRef} className="relative">
                <Field
                  label="City"
                  hint={
                    listing.cityAr
                      ? `Arabic name: ${listing.cityAr}`
                      : "Pick a city from the list"
                  }
                >
                  <input
                    className={fieldClass}
                    value={cityQuery}
                    onChange={(e) => {
                      setCityQuery(e.target.value);
                      setCityOpen(true);
                      set({
                        cityEn: e.target.value,
                        cityAr: "",
                        citySelected: false,
                      });
                    }}
                    onFocus={() => setCityOpen(true)}
                    placeholder="Search cities…"
                    autoComplete="off"
                  />
                </Field>
                {cityOpen && (
                  <ul className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-md border border-gray-300 bg-white shadow-md dark:border-gray-600 dark:bg-darkBg2">
                    {cityLoading && (
                      <li className="px-3 py-2 text-sm text-gray-500">
                        Loading…
                      </li>
                    )}
                    {!cityLoading && cityOptions.length === 0 && (
                      <li className="px-3 py-2 text-sm text-gray-500">
                        No cities found
                      </li>
                    )}
                    {cityOptions.map((city) => (
                      <li key={`${city.cityId}-${city.en}`}>
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-secondaryColor/10"
                          onClick={() => {
                            setCityQuery(city.en);
                            setCityOpen(false);
                            set({
                              cityEn: city.en,
                              cityAr: city.ar,
                              citySelected: true,
                            });
                          }}
                        >
                          {city.en}
                          {city.ar ? ` · ${city.ar}` : ""}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <Field label="District / state">
                <input
                  className={fieldClass}
                  value={listing.state}
                  onChange={setField("state")}
                />
              </Field>
              <Field label="Street">
                <input
                  className={fieldClass}
                  value={listing.street}
                  onChange={setField("street")}
                />
              </Field>
              <Field label="Apartment / unit (optional)">
                <input
                  className={fieldClass}
                  value={listing.apt}
                  onChange={setField("apt")}
                />
              </Field>
              <Field label="ZIP / postal code">
                <input
                  className={fieldClass}
                  value={listing.zip}
                  onChange={setField("zip")}
                />
              </Field>
              <Field
                label="Map pin (lat, lng)"
                className="md:col-span-2"
                hint={
                  coords
                    ? `${coords.lat}, ${coords.lng}`
                    : "Right-click a point in Google Maps and paste it here. Use this shape: 21.4858, 39.1925"
                }
                hintColor={
                  listing.latlng && !coords ? "#B42318" : undefined
                }
              >
                <input
                  className={fieldClass}
                  value={listing.latlng}
                  onChange={setField("latlng")}
                  placeholder="21.4858, 39.1925"
                />
              </Field>
            </div>
          </section>

          <section className={sectionClass}>
            <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-secondaryColor">
              Amenities and rules
            </h3>
            <p className="mb-3 text-sm text-gray-500">
              Tap to select. No IDs, no semicolons.
            </p>
            <div className="flex flex-col gap-4">
              <ChipGroup
                title="Essentials"
                items={catalogs.essentials}
                selectedIds={listing.essentials}
                onToggle={(id) => toggleChip("essentials", id)}
              />
              <ChipGroup
                title="Safety amenities"
                items={catalogs.safeties}
                selectedIds={listing.safeties}
                onToggle={(id) => toggleChip("safeties", id)}
              />
              <ChipGroup
                title="Spaces"
                items={catalogs.spaces}
                selectedIds={listing.spaces}
                onToggle={(id) => toggleChip("spaces", id)}
              />
              <ChipGroup
                title="Property rules"
                items={catalogs.propertyRules}
                selectedIds={listing.propertyRules}
                onToggle={(id) => toggleChip("propertyRules", id)}
              />
            </div>
          </section>

          <section className={sectionClass}>
            <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-secondaryColor">
              Listing content
            </h3>
            <p className="mb-3 text-sm text-gray-500">Shown to guests.</p>
            <div className="grid gap-3">
              <Field label="Title">
                <input
                  className={fieldClass}
                  value={listing.title}
                  onChange={setField("title")}
                  maxLength={250}
                  placeholder="Listing title"
                />
              </Field>
              <Field label="Description">
                <textarea
                  className={`${fieldClass} min-h-[100px]`}
                  value={listing.description}
                  onChange={setField("description")}
                  maxLength={1000}
                  placeholder="Short description for guests"
                />
              </Field>
            </div>
          </section>

          <section className={sectionClass}>
            <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-secondaryColor">
              Pricing
            </h3>
            <p className="mb-3 text-sm text-gray-500">
              Weekend prices fall back to the base price when left empty.
            </p>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Currency">
                <input className={fieldClass} value="SAR" disabled />
              </Field>
              <Field label="Base price">
                <input
                  type="number"
                  min={1}
                  max={10000}
                  className={fieldClass}
                  value={listing.basePrice}
                  onChange={setField("basePrice")}
                />
              </Field>
              <Field label="Taxes (%)" hint={`VAT from site settings (${vatPercent}%)`}>
                <input
                  type="number"
                  className={fieldClass}
                  value={listing.taxes}
                  disabled
                />
              </Field>
              <Field
                label="Weekend price (Thursday, Friday & Saturday)"
                hint="Leave empty to use the base price"
              >
                <input
                  type="number"
                  min={1}
                  max={10000}
                  className={fieldClass}
                  value={listing.weekendPrice}
                  onChange={setField("weekendPrice")}
                  placeholder="Same as base"
                />
              </Field>
              <Field label="Weekly discount (%)">
                <input
                  type="number"
                  min={0}
                  max={100}
                  className={fieldClass}
                  value={listing.weeklyDiscount}
                  onChange={setField("weeklyDiscount")}
                />
              </Field>
              <Field label="Monthly discount (%)">
                <input
                  type="number"
                  min={0}
                  max={100}
                  className={fieldClass}
                  value={listing.monthlyDiscount}
                  onChange={setField("monthlyDiscount")}
                />
              </Field>
            </div>
          </section>

          <section className={sectionClass}>
            <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-secondaryColor">
              Stay rules and extras
            </h3>
            <p className="mb-3 text-sm text-gray-500">
              Check-in window, cancellation, nights, Wi‑Fi and deposit.
            </p>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Check-in after">
                <select
                  className={fieldClass}
                  value={listing.checkInFrom}
                  onChange={setField("checkInFrom")}
                >
                  {CHECK_IN_TIME_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Check-out before"
                hint="Check-out must be at least 2 hours before check-in"
              >
                <select
                  className={fieldClass}
                  value={listing.checkInTo}
                  onChange={setField("checkInTo")}
                >
                  {CHECK_IN_TIME_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Cancellation policy">
                <select
                  className={fieldClass}
                  value={listing.cancellationPolicy}
                  onChange={setField("cancellationPolicy")}
                >
                  <option value="">Choose…</option>
                  {(catalogs.cancellationPolicies || []).map((item) => (
                    <option key={item.id} value={item.id}>
                      {catalogLabel(item)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Availability window">
                <select
                  className={fieldClass}
                  value={listing.availabilityWindow}
                  onChange={setField("availabilityWindow")}
                >
                  {AVAILABILITY_WINDOWS.map((w) => (
                    <option key={w.value} value={w.value}>
                      {w.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Minimum nights">
                <input
                  type="number"
                  min={1}
                  max={29}
                  className={fieldClass}
                  value={listing.minNights}
                  onChange={setField("minNights")}
                />
              </Field>
              <Field label="Maximum nights">
                <input
                  type="number"
                  min={1}
                  max={29}
                  className={fieldClass}
                  value={listing.maxNights}
                  onChange={setField("maxNights")}
                />
              </Field>
              <Field label="Wi-Fi name">
                <input
                  className={fieldClass}
                  value={listing.wifiName}
                  onChange={setField("wifiName")}
                />
              </Field>
              <Field label="Wi-Fi password">
                <input
                  className={fieldClass}
                  value={listing.wifiPassword}
                  onChange={setField("wifiPassword")}
                />
              </Field>
              <Field label="Security deposit">
                <YesNo
                  value={listing.securityDepositEnabled}
                  onChange={(securityDepositEnabled) =>
                    set({ securityDepositEnabled })
                  }
                />
              </Field>
            </div>
            {listing.securityDepositEnabled && (
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <Field label="Deposit amount">
                  <input
                    type="number"
                    min={0}
                    className={fieldClass}
                    value={listing.securityDepositAmount}
                    onChange={setField("securityDepositAmount")}
                  />
                </Field>
                <Field label="Security deposit notes">
                  <input
                    className={fieldClass}
                    value={listing.securityDepositNotes}
                    onChange={setField("securityDepositNotes")}
                  />
                </Field>
              </div>
            )}
          </section>
        </div>
      )}
    </article>
  );
};

export default ListingCard;
